'use client';

import { useState, useRef, useEffect } from 'react';
import { Play, Pause, Volume2, VolumeX, SkipBack, SkipForward, RotateCcw } from 'lucide-react';
import { RecipeAudio, SupportedLanguage } from '@/lib/recipe-types';
import { getRecipeUIStrings } from '@/lib/recipe-localization';

interface RecipeAudioPlayerProps {
  audio: RecipeAudio;
  lang: SupportedLanguage;
  title: string;
}

export function RecipeAudioPlayer({ audio, lang, title }: RecipeAudioPlayerProps) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(audio.durationSec || 0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [isHoveringProgress, setIsHoveringProgress] = useState(false);
  const [hoverTime, setHoverTime] = useState(0);
  const audioRef = useRef<HTMLAudioElement>(null);
  const progressBarRef = useRef<HTMLDivElement>(null);
  const strings = getRecipeUIStrings(lang);

  useEffect(() => {
    const audioElement = audioRef.current;
    if (!audioElement) return;

    const handleTimeUpdate = () => {
      setCurrentTime(audioElement.currentTime);
    };

    const handleLoadedMetadata = () => {
      setDuration(audioElement.duration);
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };

    audioElement.addEventListener('timeupdate', handleTimeUpdate);
    audioElement.addEventListener('loadedmetadata', handleLoadedMetadata);
    audioElement.addEventListener('ended', handleEnded);

    return () => {
      audioElement.removeEventListener('timeupdate', handleTimeUpdate);
      audioElement.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audioElement.removeEventListener('ended', handleEnded);
    };
  }, []);

  // Update volume when changed
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = isMuted ? 0 : volume;
    }
  }, [volume, isMuted]);

  // Update playback rate when changed
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.playbackRate = playbackRate;
    }
  }, [playbackRate]);

  const togglePlay = async () => {
    if (!audioRef.current) return;

    try {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        await audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    } catch (error) {
      console.error('Playback error:', error);
    }
  };

  const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!audioRef.current || !progressBarRef.current) return;
    
    const rect = progressBarRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = x / rect.width;
    const newTime = percentage * duration;
    
    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const handleProgressHover = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!progressBarRef.current) return;
    
    const rect = progressBarRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = Math.max(0, Math.min(1, x / rect.width));
    setHoverTime(percentage * duration);
  };

  const skipBackward = () => {
    if (audioRef.current) {
      audioRef.current.currentTime = Math.max(0, currentTime - 10);
    }
  };

  const skipForward = () => {
    if (audioRef.current) {
      audioRef.current.currentTime = Math.min(duration, currentTime + 10);
    }
  };

  const restart = () => {
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      setCurrentTime(0);
    }
  };

  const toggleMute = () => {
    setIsMuted(!isMuted);
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (newVolume > 0 && isMuted) {
      setIsMuted(false);
    }
  };

  const cyclePlaybackRate = () => {
    const rates = [0.5, 0.75, 1, 1.25, 1.5, 2];
    const currentIndex = rates.indexOf(playbackRate);
    const nextIndex = (currentIndex + 1) % rates.length;
    setPlaybackRate(rates[nextIndex]);
  };

  const formatTime = (seconds: number): string => {
    if (!isFinite(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!audio.url) {
    return (
      <div
        style={{
          background: 'linear-gradient(135deg, rgba(255, 245, 235, 0.95) 0%, rgba(255, 229, 204, 0.95) 100%)',
          backdropFilter: 'blur(10px)',
          padding: '2rem',
          borderRadius: '24px',
          border: '1px solid rgba(248, 200, 160, 0.3)',
          boxShadow: '0 8px 32px rgba(217, 119, 6, 0.08)',
          marginBottom: '2rem',
          textAlign: 'center',
        }}
      >
        <p style={{ color: '#D97706', fontSize: '1rem', margin: 0, fontWeight: 600 }}>
          {strings.audioComingSoon}
        </p>
      </div>
    );
  }

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <section
      aria-label={strings.listenToRecipe}
      style={{
        position: 'relative',
        background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 250, 245, 0.98) 100%)',
        backdropFilter: 'blur(20px)',
        padding: 'clamp(1.5rem, 3vw, 2.5rem)',
        borderRadius: '28px',
        border: '1px solid rgba(217, 119, 6, 0.15)',
        boxShadow: '0 20px 60px rgba(217, 119, 6, 0.08), 0 8px 20px rgba(0, 0, 0, 0.04)',
        marginBottom: '2rem',
        overflow: 'hidden',
      }}
    >
      {/* Ambient glow effect */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '120px',
          background: `radial-gradient(ellipse at top, rgba(217, 119, 6, 0.08) 0%, transparent 70%)`,
          pointerEvents: 'none',
        }}
      />

      {/* Header with icon and title */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.75rem', position: 'relative' }}>
        <div
          style={{
            width: '48px',
            height: '48px',
            borderRadius: '16px',
            background: 'linear-gradient(135deg, #D97706 0%, #F59E0B 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 8px 20px rgba(217, 119, 6, 0.25)',
          }}
        >
          <Volume2 size={24} color="white" strokeWidth={2.5} aria-hidden="true" />
        </div>
        <h3
          style={{
            fontSize: 'clamp(1.125rem, 2.5vw, 1.375rem)',
            fontWeight: 800,
            background: 'linear-gradient(135deg, #D97706 0%, #F59E0B 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            margin: 0,
            letterSpacing: '-0.01em',
          }}
        >
          {strings.listenToRecipe}
        </h3>
      </div>

      <audio ref={audioRef} src={audio.url} preload="metadata" />

      {/* Recipe Title */}
      <p
        style={{
          fontSize: 'clamp(0.9375rem, 2vw, 1.0625rem)',
          color: '#78350F',
          margin: '0 0 1.5rem 0',
          fontWeight: 600,
          lineHeight: 1.5,
        }}
      >
        {title}
      </p>

      {/* Progress Bar with hover preview */}
      <div
        ref={progressBarRef}
        onClick={handleSeek}
        onMouseMove={handleProgressHover}
        onMouseEnter={() => setIsHoveringProgress(true)}
        onMouseLeave={() => setIsHoveringProgress(false)}
        style={{
          position: 'relative',
          width: '100%',
          height: '12px',
          borderRadius: '8px',
          background: 'rgba(217, 119, 6, 0.1)',
          cursor: 'pointer',
          marginBottom: '1.5rem',
          overflow: 'visible',
        }}
      >
        {/* Progress fill */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            height: '100%',
            width: `${progress}%`,
            borderRadius: '8px',
            background: 'linear-gradient(90deg, #D97706 0%, #F59E0B 100%)',
            transition: 'width 0.1s linear',
            boxShadow: '0 2px 8px rgba(217, 119, 6, 0.3)',
          }}
        />

        {/* Hover time tooltip */}
        {isHoveringProgress && (
          <div
            style={{
              position: 'absolute',
              bottom: '20px',
              left: `${(hoverTime / duration) * 100}%`,
              transform: 'translateX(-50%)',
              padding: '0.375rem 0.75rem',
              background: 'rgba(0, 0, 0, 0.85)',
              color: 'white',
              fontSize: '0.75rem',
              fontWeight: 600,
              borderRadius: '8px',
              pointerEvents: 'none',
              whiteSpace: 'nowrap',
              zIndex: 10,
            }}
          >
            {formatTime(hoverTime)}
          </div>
        )}

        {/* Playhead */}
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: `${progress}%`,
            transform: 'translate(-50%, -50%)',
            width: '20px',
            height: '20px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #FFF 0%, #FAFAFA 100%)',
            border: '3px solid #D97706',
            boxShadow: '0 4px 12px rgba(217, 119, 6, 0.4)',
            transition: 'transform 0.2s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translate(-50%, -50%) scale(1.15)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translate(-50%, -50%) scale(1)';
          }}
        />
      </div>

      {/* Time display */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontSize: 'clamp(0.75rem, 1.5vw, 0.875rem)',
          fontWeight: 700,
          color: '#92400E',
          marginBottom: '1.5rem',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        <span>{formatTime(currentTime)}</span>
        <span style={{ color: '#D97706' }}>{formatTime(duration)}</span>
      </div>

      {/* Main controls */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 'clamp(0.75rem, 2vw, 1rem)', marginBottom: '1.25rem' }}>
        {/* Restart button */}
        <button
          onClick={restart}
          aria-label="Restart"
          style={{
            width: 'clamp(40px, 8vw, 48px)',
            height: 'clamp(40px, 8vw, 48px)',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, rgba(217, 119, 6, 0.1) 0%, rgba(245, 158, 11, 0.1) 100%)',
            border: '2px solid rgba(217, 119, 6, 0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            color: '#D97706',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'linear-gradient(135deg, rgba(217, 119, 6, 0.2) 0%, rgba(245, 158, 11, 0.2) 100%)';
            e.currentTarget.style.borderColor = '#D97706';
            e.currentTarget.style.transform = 'scale(1.05)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'linear-gradient(135deg, rgba(217, 119, 6, 0.1) 0%, rgba(245, 158, 11, 0.1) 100%)';
            e.currentTarget.style.borderColor = 'rgba(217, 119, 6, 0.2)';
            e.currentTarget.style.transform = 'scale(1)';
          }}
        >
          <RotateCcw size={18} strokeWidth={2.5} />
        </button>

        {/* Skip backward */}
        <button
          onClick={skipBackward}
          aria-label="Skip backward 10 seconds"
          style={{
            width: 'clamp(44px, 9vw, 52px)',
            height: 'clamp(44px, 9vw, 52px)',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 250, 245, 0.9) 100%)',
            border: '2px solid rgba(217, 119, 6, 0.3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            color: '#D97706',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'linear-gradient(135deg, #FFF 0%, #FFF 100%)';
            e.currentTarget.style.borderColor = '#D97706';
            e.currentTarget.style.transform = 'scale(1.05)';
            e.currentTarget.style.boxShadow = '0 6px 16px rgba(217, 119, 6, 0.15)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 250, 245, 0.9) 100%)';
            e.currentTarget.style.borderColor = 'rgba(217, 119, 6, 0.3)';
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.05)';
          }}
        >
          <SkipBack size={20} strokeWidth={2.5} fill="currentColor" />
        </button>

        {/* Play/Pause - Hero button */}
        <button
          onClick={togglePlay}
          aria-label={isPlaying ? strings.pauseAudio : strings.playAudio}
          style={{
            width: 'clamp(64px, 12vw, 72px)',
            height: 'clamp(64px, 12vw, 72px)',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #D97706 0%, #F59E0B 100%)',
            border: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            boxShadow: '0 12px 28px rgba(217, 119, 6, 0.35), 0 4px 12px rgba(217, 119, 6, 0.2)',
            position: 'relative',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'scale(1.08)';
            e.currentTarget.style.boxShadow = '0 16px 36px rgba(217, 119, 6, 0.45), 0 6px 16px rgba(217, 119, 6, 0.3)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.boxShadow = '0 12px 28px rgba(217, 119, 6, 0.35), 0 4px 12px rgba(217, 119, 6, 0.2)';
          }}
        >
          {isPlaying ? (
            <Pause size={28} color="white" fill="white" strokeWidth={2.5} />
          ) : (
            <Play size={28} color="white" fill="white" strokeWidth={2.5} style={{ marginLeft: '3px' }} />
          )}
        </button>

        {/* Skip forward */}
        <button
          onClick={skipForward}
          aria-label="Skip forward 10 seconds"
          style={{
            width: 'clamp(44px, 9vw, 52px)',
            height: 'clamp(44px, 9vw, 52px)',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 250, 245, 0.9) 100%)',
            border: '2px solid rgba(217, 119, 6, 0.3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            color: '#D97706',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'linear-gradient(135deg, #FFF 0%, #FFF 100%)';
            e.currentTarget.style.borderColor = '#D97706';
            e.currentTarget.style.transform = 'scale(1.05)';
            e.currentTarget.style.boxShadow = '0 6px 16px rgba(217, 119, 6, 0.15)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 250, 245, 0.9) 100%)';
            e.currentTarget.style.borderColor = 'rgba(217, 119, 6, 0.3)';
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.05)';
          }}
        >
          <SkipForward size={20} strokeWidth={2.5} fill="currentColor" />
        </button>

        {/* Playback speed */}
        <button
          onClick={cyclePlaybackRate}
          aria-label={`Playback speed: ${playbackRate}x`}
          style={{
            width: 'clamp(40px, 8vw, 48px)',
            height: 'clamp(40px, 8vw, 48px)',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, rgba(217, 119, 6, 0.1) 0%, rgba(245, 158, 11, 0.1) 100%)',
            border: '2px solid rgba(217, 119, 6, 0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            color: '#D97706',
            fontSize: 'clamp(0.6875rem, 1.5vw, 0.75rem)',
            fontWeight: 800,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'linear-gradient(135deg, rgba(217, 119, 6, 0.2) 0%, rgba(245, 158, 11, 0.2) 100%)';
            e.currentTarget.style.borderColor = '#D97706';
            e.currentTarget.style.transform = 'scale(1.05)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'linear-gradient(135deg, rgba(217, 119, 6, 0.1) 0%, rgba(245, 158, 11, 0.1) 100%)';
            e.currentTarget.style.borderColor = 'rgba(217, 119, 6, 0.2)';
            e.currentTarget.style.transform = 'scale(1)';
          }}
        >
          {playbackRate}×
        </button>
      </div>

      {/* Volume control */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '0.75rem 1rem', background: 'rgba(217, 119, 6, 0.05)', borderRadius: '16px' }}>
        <button
          onClick={toggleMute}
          aria-label={isMuted ? 'Unmute' : 'Mute'}
          style={{
            width: '36px',
            height: '36px',
            borderRadius: '50%',
            background: 'transparent',
            border: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            transition: 'all 0.2s',
            color: isMuted ? '#92400E' : '#D97706',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(217, 119, 6, 0.1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent';
          }}
        >
          {isMuted ? <VolumeX size={20} strokeWidth={2.5} /> : <Volume2 size={20} strokeWidth={2.5} />}
        </button>

        <input
          type="range"
          min="0"
          max="1"
          step="0.01"
          value={isMuted ? 0 : volume}
          onChange={handleVolumeChange}
          aria-label="Volume"
          style={{
            flex: 1,
            height: '6px',
            borderRadius: '3px',
            background: `linear-gradient(to right, #D97706 0%, #D97706 ${(isMuted ? 0 : volume) * 100}%, rgba(217, 119, 6, 0.2) ${(isMuted ? 0 : volume) * 100}%, rgba(217, 119, 6, 0.2) 100%)`,
            outline: 'none',
            cursor: 'pointer',
            WebkitAppearance: 'none',
            appearance: 'none',
          }}
        />

        <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#92400E', minWidth: '32px', textAlign: 'right' }}>
          {Math.round((isMuted ? 0 : volume) * 100)}%
        </span>
      </div>

      <style jsx>{`
        input[type='range']::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background: linear-gradient(135deg, #D97706 0%, #F59E0B 100%);
          cursor: pointer;
          border: 3px solid white;
          box-shadow: 0 2px 8px rgba(217, 119, 6, 0.4);
          transition: all 0.2s;
        }

        input[type='range']::-webkit-slider-thumb:hover {
          transform: scale(1.15);
          box-shadow: 0 4px 12px rgba(217, 119, 6, 0.5);
        }

        input[type='range']::-moz-range-thumb {
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background: linear-gradient(135deg, #D97706 0%, #F59E0B 100%);
          cursor: pointer;
          border: 3px solid white;
          box-shadow: 0 2px 8px rgba(217, 119, 6, 0.4);
          transition: all 0.2s;
        }

        input[type='range']::-moz-range-thumb:hover {
          transform: scale(1.15);
          box-shadow: 0 4px 12px rgba(217, 119, 6, 0.5);
        }

        input[type='range']:focus::-webkit-slider-thumb {
          outline: 2px solid #D97706;
          outline-offset: 2px;
        }

        input[type='range']:focus::-moz-range-thumb {
          outline: 2px solid #D97706;
          outline-offset: 2px;
        }
      `}</style>
    </section>
  );
}

